import React, { useState, useEffect, useCallback } from 'react';
import ServiceHistoryTable from './ServiceHistoryTable';
import ServiceNoteModal from './ServiceNoteModal';
import ServiceAdicionalPanel, { AdicionalInitialValues } from './Adicional/ServiceAdicionalPanel';
import ServiceEnaexCodePanel from './Adicional/ServiceEnaexCodePanel';
import useServiceNotesService from '../../services/useServiceNotesService';
import { ServiceNote } from '../../models/ServiceNoteModel';
import type { ServiceResponse } from '../../hooks/useFetch';
import './styles/ServiceNotesHistoryTabs.css';

interface ServiceNotesHistoryTabsProps {
  serviceId: number;
  /** Si es false, no se muestra la pestaña Adicional (solo para la empresa configurada, ej. El Abra). */
  showAdicionalTab?: boolean;
  /** Contenido de la pestaña: El Abra (catálogos) o ENAEX (código en tabla propia). */
  adicionalKind?: 'el_abra' | 'enaex' | null;
  adicionalInitialValues?: AdicionalInitialValues | null;
  /** Code ENAEX inicial (GET edit). */
  enaexInitialCode?: string | null;
  onAdicionalSaved?: () => void;
  valePdfUrl?: string | null;
}

const ServiceNotesHistoryTabs: React.FC<ServiceNotesHistoryTabsProps> = ({
  serviceId,
  showAdicionalTab = false,
  adicionalKind = 'el_abra',
  adicionalInitialValues,
  enaexInitialCode = null,
  onAdicionalSaved,
  valePdfUrl,
}) => {
  const [activeTab, setActiveTab] = useState<'history' | 'notes' | 'adicional' | 'pdf'>('history');
  const [notes, setNotes] = useState<ServiceNote[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const { listByService, fetching } = useServiceNotesService();

  const loadNotes = useCallback(() => {
    listByService(serviceId, {
      onSuccess: (r: ServiceResponse) => {
        const data = r?.data;
        if (data && Array.isArray(data.notes)) {
          setNotes(data.notes);
        }
      },
    });
  }, [serviceId, listByService]);

  useEffect(() => {
    if (activeTab === 'notes') {
      loadNotes();
    }
  }, [activeTab, serviceId, loadNotes]);

  useEffect(() => {
    if (!showAdicionalTab && activeTab === 'adicional') {
      setActiveTab('history');
    }
  }, [showAdicionalTab, activeTab]);

  const formatNoteDate = (iso: string) => {
    try {
      const date = new Date(iso);
      return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch {
      return iso;
    }
  };

  const handleNoteAdded = () => {
    loadNotes();
  };

  return (
    <div className="service-notes-history-tabs">
      <div className="tabs-header">
        <div className="tabs-nav">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'history' ? 'tab-btn-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Historial de Cambios
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'notes' ? 'tab-btn-active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" />
              <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" />
              <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" />
              <polyline points="10 9 9 9 8 9" stroke="currentColor" strokeWidth="2" />
            </svg>
            Notas
            {notes.length > 0 && (
              <span className="tab-badge">{notes.length}</span>
            )}
          </button>
          {showAdicionalTab && (
            <button
              type="button"
              className={`tab-btn ${activeTab === 'adicional' ? 'tab-btn-active' : ''}`}
              onClick={() => setActiveTab('adicional')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Adicional
            </button>
          )}
          {/*<button
            type="button"
            className={`tab-btn ${activeTab === 'pdf' ? 'tab-btn-active' : ''}`}
            onClick={() => setActiveTab('pdf')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" />
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" />
              <path d="M9 14h6M9 18h6M10 10h4" stroke="currentColor" strokeWidth="2" />
            </svg>
            PDF
          </button>*/}
        </div>
      </div>

      <div className="tabs-content">
        {activeTab === 'history' && (
          <div className="tab-panel tab-panel-history">
            <ServiceHistoryTable serviceId={serviceId} embedded />
          </div>
        )}

        {showAdicionalTab && activeTab === 'adicional' && (
          <div className="tab-panel tab-panel-adicional">
            {adicionalKind === 'enaex' ? (
              <ServiceEnaexCodePanel
                serviceId={serviceId}
                initialCode={enaexInitialCode}
                onSaved={onAdicionalSaved}
              />
            ) : (
              <ServiceAdicionalPanel
                serviceId={serviceId}
                initialValues={adicionalInitialValues}
                onSaved={onAdicionalSaved}
              />
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="tab-panel tab-panel-notes">
            <div className="notes-add-block">
              <button
                type="button"
                className="notes-add-btn notes-add-btn-modal"
                onClick={() => setModalOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" />
                  <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" />
                </svg>
                Agregar nota
              </button>
            </div>

            <div className="notes-list-header">
              <h4 className="notes-list-title">Comentarios y notas</h4>
              <span className="notes-list-count">
                {fetching.list ? '...' : `${notes.length} nota${notes.length !== 1 ? 's' : ''}`}
              </span>
            </div>

            <ul className="notes-list">
              {fetching.list ? (
                <li className="notes-empty">
                  <p>Cargando notas...</p>
                </li>
              ) : notes.length === 0 ? (
                <li className="notes-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <p>No hay notas aún. Añade la primera.</p>
                </li>
              ) : (
                notes.map((note, index) => {
                  const author = note.user_full_name || 'Sistema';
                  const initial = author.trim().charAt(0).toUpperCase() || 'N';
                  return (
                    <li
                      key={note.id}
                      className="note-item"
                      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                    >
                      <div className="note-item__accent" aria-hidden />
                      <div className="note-meta">
                        <div className="note-author">
                          <span className="note-avatar" aria-hidden>
                            {initial}
                          </span>
                          <span className="note-user">{author}</span>
                        </div>
                        <span className="note-date">{formatNoteDate(note.created_at)}</span>
                      </div>
                      <p className="note-text">{note.text}</p>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}

        {activeTab === 'pdf' && (
          <div className="tab-panel tab-panel-pdf">
            {valePdfUrl ? (
              <div className="pdf-panel">
                <p className="pdf-panel-title">Documento firmado disponible</p>
                <a className="pdf-download-btn" href={valePdfUrl} target="_blank" rel="noreferrer">
                  Descargar PDF del vale
                </a>
              </div>
            ) : (
              <div className="pdf-panel pdf-panel-empty">
                <p>Aún no existe un PDF firmado para este servicio.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <ServiceNoteModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        serviceId={serviceId}
        onSuccess={handleNoteAdded}
      />
    </div>
  );
};

export default ServiceNotesHistoryTabs;
